import {
  computeEpaCorrectionDeadline,
  type EpaCorrectionSection,
  parseEpaCorrectionSections,
} from "encamp-shared/src/waste/epaCorrectionRequests";
import { ManifestComplianceEscalation, type SubmissionStatus } from "generated-graphql/graphql";

import type { DiscrepancyStatus } from "./DiscrepancyReviewDialog.helpers";

type ComplianceItemBase = {
  id: string;
  processedManifestId: string;
  manifestTrackingNumber: string | null;
  epaId: string | null;
  generatorName: string | null;
  tsdfName: string | null;
  sourceDocumentId: string | null;
  sourceDocumentFileExtension: string | null;
  status: DiscrepancyStatus;
};

export type ComplianceItemMissing = ComplianceItemBase & {
  kind: "missing";
  escalation: ManifestComplianceEscalation | null;
  shippedDate: string | null;
  transporterContactedAt: string | null;
  transporterContactedBy: string | null;
  transporterContactProofDocumentId: string | null;
  exceptionReportSubmissionId: string | null;
  exceptionReportSubmissionStatus: SubmissionStatus | null;
  exceptionReportTitle: string | null;
};

export type ComplianceItemEpa = ComplianceItemBase & {
  kind: "epaCorrection";
  requestedAt: string;
  deadline: string;
  initiator: string;
  sections: EpaCorrectionSection[];
};

export type ComplianceItem = ComplianceItemMissing | ComplianceItemEpa;

export type DeadlineBand = "crit" | "warn" | "due";

export function daysBetween(start: Date, end: Date): number {
  const MS_PER_DAY = 1000 * 60 * 60 * 24;
  // Anchor at UTC midnight so DST/timezone shifts don't push the boundary across a calendar day.
  const a = Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate());
  const b = Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate());
  return Math.round((b - a) / MS_PER_DAY);
}

/**
 * Days until the earliest EPA correction deadline among the given request
 * dates, anchored at UTC midnights (same convention as daysBetween, so the
 * manifest detail footer's countdown always matches the compliance cards).
 * Negative = overdue; null when there are no open requests.
 */
export function daysUntilEarliestEpaCorrectionDeadline(
  requestDates: readonly Date[],
  now: Date
): number | null {
  if (requestDates.length === 0) return null;
  const earliest = requestDates
    .map(computeEpaCorrectionDeadline)
    .reduce((a, b) => (a.getTime() <= b.getTime() ? a : b));
  return daysBetween(now, earliest);
}

export function getDeadlineBand(item: ComplianceItem, now: Date): DeadlineBand {
  if (item.kind === "missing") {
    if (item.escalation === ManifestComplianceEscalation.Overdue_60) return "crit";
    if (item.escalation === ManifestComplianceEscalation.Overdue_45) return "warn";
    return "due";
  }
  const remaining = daysBetween(now, new Date(item.deadline));
  if (remaining < 0) return "crit";
  if (remaining <= 7) return "warn";
  return "due";
}

/** Human-readable label for an escalation level — never show the raw enum to users. */
export function getEscalationLabel(escalation: ManifestComplianceEscalation | null): string {
  switch (escalation) {
    case ManifestComplianceEscalation.Overdue_45:
      return "Overdue 45+ days";
    case ManifestComplianceEscalation.Overdue_60:
      return "Overdue 60+ days";
    case null:
      return "Overdue";
  }
}

export function getDeadlineDisplay(item: ComplianceItem, now: Date): string {
  if (item.kind === "missing") {
    if (!item.shippedDate) return getEscalationLabel(item.escalation);
    const day = daysBetween(new Date(item.shippedDate), now);
    return `Day ${day} of 60`;
  }
  const remaining = daysBetween(now, new Date(item.deadline));
  const absDays = Math.abs(remaining);
  const dayLabel = absDays === 1 ? "day" : "days";
  if (remaining < 0) return `Overdue (${absDays} ${dayLabel})`;
  return `${remaining} ${dayLabel} remaining`;
}

function urgencyKey(item: ComplianceItem, now: Date): number {
  if (item.kind === "missing") {
    if (item.escalation === ManifestComplianceEscalation.Overdue_60) return -1_000_000;
    if (item.escalation === ManifestComplianceEscalation.Overdue_45) return -500_000;
    if (item.shippedDate) {
      return 60 - daysBetween(new Date(item.shippedDate), now);
    }
    // Finite sentinel — Infinity would produce NaN in the comparator and make sort engine-defined.
    return Number.MAX_SAFE_INTEGER;
  }
  return daysBetween(now, new Date(item.deadline));
}

export function sortByDeadlineAscending(
  items: readonly ComplianceItem[],
  now: Date
): ComplianceItem[] {
  return [...items].sort((a, b) => {
    const delta = urgencyKey(a, now) - urgencyKey(b, now);
    if (delta !== 0) return delta;
    return a.id.localeCompare(b.id);
  });
}

export type EpaCorrectionDiscrepancyInput = {
  id: string;
  processedManifestId: string;
  manifestTrackingNumber?: string | null;
  epaId?: string | null;
  facilityName?: string | null;
  sourceDocumentId?: string | null;
  sourceDocumentFileExtension?: string | null;
  status: DiscrepancyStatus;
  wasteManifestCorrectionRequest?: {
    requestDate: string | number | Date;
    initiatorRole: string;
    sections: unknown;
  } | null;
};

export function mapEpaCorrectionDiscrepancy(
  d: EpaCorrectionDiscrepancyInput
): ComplianceItemEpa | null {
  const wmcr = d.wasteManifestCorrectionRequest;
  if (!wmcr) return null;

  const requestDate = new Date(wmcr.requestDate);
  if (Number.isNaN(requestDate.getTime())) return null;

  const deadline = computeEpaCorrectionDeadline(requestDate);

  return {
    kind: "epaCorrection",
    id: d.id,
    processedManifestId: d.processedManifestId,
    manifestTrackingNumber: d.manifestTrackingNumber ?? null,
    epaId: d.epaId ?? null,
    generatorName: d.facilityName ?? null,
    tsdfName: null,
    sourceDocumentId: d.sourceDocumentId ?? null,
    sourceDocumentFileExtension: d.sourceDocumentFileExtension ?? null,
    status: d.status,
    requestedAt: requestDate.toISOString().slice(0, 10),
    deadline: deadline.toISOString().slice(0, 10),
    initiator: wmcr.initiatorRole,
    sections: parseEpaCorrectionSections(wmcr.sections),
  };
}
